import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TextField, IconButton, Dialog, DialogContent, DialogTitle, Button,
  Alert, CircularProgress, MenuItem, TablePagination
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { CLIENT_ADDRESS_LINES, CLIENT_NAME, CLIENT_TELEPHONE } from '../config/clientInfo';

const PAYMENT_METHODS = ['Cash', 'Card'];

const formatAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
};

function Billhistory() {
  const ReturnprintsRef = useRef();
  const [billHeaders, setBillHeaders] = useState([]);
  const [totalBills, setTotalBills] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedBillItems, setSelectedBillItems] = useState([]);
  const [selectedBillMeta, setSelectedBillMeta] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [paymentMethodUpdates, setPaymentMethodUpdates] = useState({});

  const userRole = Number(JSON.parse(localStorage.getItem("AutoParts"))?.role);

  useEffect(() => {
    let isMounted = true;

    const fetchBillHeaders = async () => {
      setHistoryLoading(true);
      setHistoryError('');
      try {
        const res = await axios.get('https://serverrion.mylksystems.store/billing/getBillHeaders', {
          params: {
            page,
            limit: rowsPerPage,
            search: searchTerm || undefined,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
          },
        });
        const headers = Array.isArray(res.data) ? res.data : res.data?.bills || [];
        const total = Array.isArray(res.data) ? headers.length : Number(res.data?.total || 0);

        if (isMounted) {
          setBillHeaders(headers);
          setTotalBills(total);
        }
      } catch (err) {
        console.error('Error fetching bill headers:', err);
        if (isMounted) {
          setBillHeaders([]);
          setTotalBills(0);
          setHistoryError('Unable to load bill history. Check that the server is running and try again.');
        }
      } finally {
        if (isMounted) {
          setHistoryLoading(false);
        }
      }
    };

    fetchBillHeaders();

    return () => {
      isMounted = false;
    };
  }, [page, rowsPerPage, searchTerm, fromDate, toDate]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
    setPage(0);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFromDate('');
    setToDate('');
    setPage(0);
  };

  const handlePageChange = (event, nextPage) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const loadBill = async (inv_no) => {
    try {
      const res = await axios.post(`https://serverrion.mylksystems.store/billing/getSingleBill`, { inv_no });
      if (res.data && res.data.length > 0) {
        const itemRows = res.data.filter((row) => row.item_name);
        const meta = {
          inv_no: res.data[0].billNo,
          username: res.data[0].username,
          inv_date: res.data[0].inv_date,
          gross_total: res.data[0].gross_total,
          discount: res.data[0].discount,
          net_total: res.data[0].net_total,
          Service_chrg: res.data[0].Service_chrg,
          excessFoodTag: res.data[0].excessFoodTag,
          paymentMethod: res.data[0].paymentMethod,
        };
        setSelectedBillItems(itemRows);
        setSelectedBillMeta(meta);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error fetching single bill:', err);
      return false;
    }
  };

  const handlePrintTrigger = useReactToPrint({
    content: () => ReturnprintsRef.current,
  });

  const handlePrint = async (inv_no) => {
    const ok = await loadBill(inv_no);
    if (ok) {
      setTimeout(() => handlePrintTrigger(), 200);
    } else {
      alert('Unable to load bill details.');
    }
  };

  const handleView = async (inv_no) => {
    const ok = await loadBill(inv_no);
    if (ok) {
      setViewOpen(true);
    } else {
      alert('Unable to load bill details.');
    }
  };

  const updateBillPaymentMethodInState = (invNo, nextPaymentMethod) => {
    setBillHeaders((previousBills) =>
      previousBills.map((bill) =>
        bill.inv_no === invNo ? { ...bill, paymentMethod: nextPaymentMethod } : bill
      )
    );

    setSelectedBillMeta((previousMeta) =>
      previousMeta?.inv_no === invNo
        ? { ...previousMeta, paymentMethod: nextPaymentMethod }
        : previousMeta
    );
  };

  const handlePaymentMethodChange = async (bill, nextPaymentMethod) => {
    const invNo = bill.inv_no;
    const previousPaymentMethod = bill.paymentMethod;

    if (!invNo || !nextPaymentMethod || nextPaymentMethod === previousPaymentMethod) {
      return;
    }

    updateBillPaymentMethodInState(invNo, nextPaymentMethod);
    setPaymentMethodUpdates((previous) => ({ ...previous, [invNo]: true }));

    try {
      await axios.post('https://serverrion.mylksystems.store/billing/updatePaymentMethod', {
        inv_no: invNo,
        paymentMethod: nextPaymentMethod,
      });
    } catch (err) {
      console.error('Error updating payment method:', err);
      updateBillPaymentMethodInState(invNo, previousPaymentMethod);
      alert('Unable to update payment method.');
    } finally {
      setPaymentMethodUpdates((previous) => {
        const next = { ...previous };
        delete next[invNo];
        return next;
      });
    }
  };

  const renderBillTable = ({ withRef }) => {
    if (!selectedBillMeta) return null;

    return (
        <table
          border={0}
          width="300"
          style={{ fontFamily: 'Consolas', backgroundColor: 'white', fontWeight: 'bold' }}
          ref={withRef ? ReturnprintsRef : undefined}
        >
          <tbody>
             <tr>
              <td colSpan={4} align='center' style={{ fontSize: 14, fontWeight: 'bold' }}><label style={{ fontSize: 20 }}> </label><br></br> {CLIENT_NAME}<br></br>
              {CLIENT_ADDRESS_LINES[0]}<br></br>
              {CLIENT_ADDRESS_LINES[1]}<br></br>
                <img src={require('../Assests/whatsapp.png')} alt='' width={10} style={{marginRight : 10}}></img>
              Tel. {CLIENT_TELEPHONE}  <br></br>
                <img src={require('../Assests/Call.png')} alt='' width={10} style={{marginRight : 10}}></img>
              Tel. {CLIENT_TELEPHONE}  <br></br>

               </td>
              
            </tr>
            <tr><td colSpan={4}>---------------------------------</td></tr>
            <tr><td colSpan={4}>Cashier: {selectedBillMeta.username}</td></tr>
            <tr><td colSpan={4}>Date: {selectedBillMeta.inv_date}</td></tr>
            <tr><td colSpan={4}>Invoice no: {selectedBillMeta.inv_no}</td></tr>
            <tr><td colSpan={4}>Payment: {selectedBillMeta.paymentMethod || '-'}</td></tr>
            <tr><td colSpan={4}>---------------------------------</td></tr>

            {selectedBillItems.length > 0 ? (
              selectedBillItems.map((e, i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td colSpan={4}>{i + 1}). {e.item_name}</td>
                  </tr>
                  <tr>
                    <td align="right">{e.qty}</td>
                    <td align="center">{formatAmount(e.unitprice)}</td>
                    <td align="right">{formatAmount(e.total)}</td>
                  </tr>
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={4} align="center">No active items found</td>
              </tr>
            )}

            <tr><td colSpan={4}>---------------------------------</td></tr>
            <tr>
              <td colSpan={2} align="left">Gross Rs.</td>
              <td colSpan={2} align="right"> {formatAmount(selectedBillMeta.gross_total)}</td>
            </tr>
            <tr>
              <td colSpan={2} align="left">Discount Rs.</td>
              <td colSpan={2} align="right"> ({formatAmount(selectedBillMeta.discount)})</td>
            </tr>
            {Number(selectedBillMeta.Service_chrg || 0) > 0 ? (
              <tr>
                <td colSpan={2} align="Left">Service Charge (10%) Rs.</td>
                <td colSpan={2} align="right"> {formatAmount(selectedBillMeta.Service_chrg)}</td>
              </tr>
            ) : null}
            {Number(selectedBillMeta.excessFoodTag || 0) === 1 ? (
              <tr>
                <td colSpan={4} align="center">Excess Food Donation</td>
              </tr>
            ) : null}
              <tr><td colSpan={4}>---------------------------------</td></tr>
            

            <tr><td colSpan={4} align="right"><b>Total Rs. {formatAmount(selectedBillMeta.net_total)}</b></td></tr>
               <tr>
              <td colSpan={4} align='right'>==========</td>
            </tr>
            <tr><td colSpan={4} align="center" style={{fontSize : 12}}>Thank you, Come Again.</td></tr>
            <tr><td colSpan={4}>---------------------------------</td></tr>
          </tbody>
        </table>
    );
  };

  const renderPrintableBill = () => {
    if (!selectedBillMeta) return null;
    return (
      <div style={{ display: 'none' }}>
        {renderBillTable({ withRef: true })}
      </div>
    );
  };

  return (
    <Box p={2} style={{ paddingTop: '100px' }}>
      <div style={{ textAlign: 'left', marginBottom: '20px', fontSize: '30px', fontWeight: 'bold' }}>
        Bill History
      </div>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label="Search Invoice No"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ minWidth: 260, flex: '1 1 260px' }}
        />
        <TextField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={handleFromDateChange}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <TextField
          label="To Date"
          type="date"
          value={toDate}
          onChange={handleToDateChange}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
        <Button
          variant="outlined"
          onClick={clearFilters}
          disabled={!searchTerm && !fromDate && !toDate}
          sx={{ height: 56 }}
        >
          Clear
        </Button>
      </Box>

      {historyError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {historyError}
        </Alert>
      ) : null}

      {historyLoading ? (
        <Paper sx={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Paper>
      ) : (
        <Paper>
          <TableContainer sx={{ maxHeight: 600, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 'bold' }}>Invoice No</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Inv Date</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Gross</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Discount</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Net</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Service Chrg</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Payment Method</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Cashier</TableCell>
                  <TableCell style={{ fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billHeaders.length > 0 ? billHeaders.map((bill) => (
                  <TableRow key={bill.inv_no}>
                    <TableCell>{bill.inv_no}</TableCell>
                    <TableCell>{bill.inv_date}</TableCell>
                    <TableCell>{formatAmount(bill.gross_total)}</TableCell>
                    <TableCell>{formatAmount(bill.discount)}</TableCell>
                    <TableCell>{formatAmount(bill.net_total)}</TableCell>
                    <TableCell>{formatAmount(bill.Service_chrg)}</TableCell>
                    <TableCell>
                      {userRole === 1 ? (
                        <TextField
                          select
                          size="small"
                          value={PAYMENT_METHODS.includes(bill.paymentMethod) ? bill.paymentMethod : ''}
                          onChange={(event) => handlePaymentMethodChange(bill, event.target.value)}
                          disabled={Boolean(paymentMethodUpdates[bill.inv_no])}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="" disabled>
                            Select
                          </MenuItem>
                          {PAYMENT_METHODS.map((method) => (
                            <MenuItem key={method} value={method}>
                              {method}
                            </MenuItem>
                          ))}
                        </TextField>
                      ) : (
                        bill.paymentMethod || '-'
                      )}
                    </TableCell>
                    <TableCell>{bill.username}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleView(bill.inv_no)}>
                        <VisibilityIcon />
                      </IconButton>
                      {userRole === 1 ? (
                        <IconButton onClick={() => handlePrint(bill.inv_no)}>
                          <PrintIcon />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No bills found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalBills}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Paper>
      )}

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>View Bill</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {renderBillTable({ withRef: false })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" onClick={() => setViewOpen(false)}>Close</Button>
          </Box>
        </DialogContent>
      </Dialog>

      {renderPrintableBill()}
    </Box>
  );
}

export default Billhistory;
